// ExportMenu.tsx
import { FunctionComponent, useState, useCallback, useEffect } from 'react'; 
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import Button from '../../../ui/components/Button';
import MenuOpen, { MenuOpenProps } from '../../../ui/components/MenuOpen';
import Dropdown from '../../../ui/components/Dropdown';
import SelectButtonGroup from '../../../ui/components/SelectButtonGroup';
import GridSelect, { GridSelection } from '../../../ui/components/GridSelect';
import LabelRow from '../../../ui/components/LabelRow';

const MenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 6px 10px;
    gap: 4px;
`;

const MenuTitle = styled.div`
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 12px;
    color: ${Colors.grey6};
    text-transform: uppercase;
    width: 100%;
    text-align: left;
    padding: 2px 2px 0 2px;
`;

const DownloadButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 4px 0;
    width: 100%;
`;

const ExportOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 8px;
`;

const Item = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 2px;
`;

interface ExportMenuProps extends MenuOpenProps {
    autoRows: number;
    autoCols: number;
    autoSheetWidth: number;
    autoSheetHeight: number;
    totalFrames: number;
    isExporting: boolean;
    onExport: (format: string, settings: any) => void;
}

const exportOptions = [
    { id: 'sprite_sheet', value: 'sprite sheet', subValue: '.png' },
    { id: 'video', value: 'video', subValue: '.mp4' },
    { id: 'webm', value: 'video', subValue: '.webm' },
    { id: 'gif', value: 'animation', subValue: '.gif' },
    { id: 'pngs', value: 'images', subValue: '.zip' },
];

const SPRITE_SHEET_ID = 'sprite_sheet';

const ExportMenu: FunctionComponent<ExportMenuProps> = ({
    anchorEl,
    open,
    onClose,
    onMenuLeave,
    autoRows,     
    autoCols,     
    autoSheetWidth,    
    autoSheetHeight,   
    totalFrames,
    isExporting,
    onExport,     
}) => {
    const [selectedFormatId, setSelectedFormatId] = useState<string>(exportOptions[0].id);
    
    const getInitialGridSelection = useCallback((): GridSelection => ({
        startRow: 0,
        startCol: 0,
        endRow: autoRows > 0 ? autoRows - 1 : 0,
        endCol: autoCols > 0 ? autoCols - 1 : 0,
    }), [autoRows, autoCols]);

    const [selectedGrid, setSelectedGrid] = useState<GridSelection>(getInitialGridSelection());

    const [exportWidth, setExportWidth] = useState<string | number>(autoSheetWidth);
    const [exportHeight, setExportHeight] = useState<string | number>(autoSheetHeight);

    useEffect(() => {
        setExportWidth(autoSheetWidth);
        setExportHeight(autoSheetHeight);
        setSelectedGrid(getInitialGridSelection());
    }, [autoSheetWidth, autoSheetHeight, autoRows, autoCols, getInitialGridSelection]);
    
    const handleGridSelect = useCallback((selection: GridSelection) => {
        // console.log('Grid selection updated:', selection);
        setSelectedGrid(selection);
    }, []);

    const handleDownload = () => {
        const settings = {
            rows: selectedGrid.endRow - selectedGrid.startRow + 1,
            cols: selectedGrid.endCol - selectedGrid.startCol + 1,
            sheetWidth: exportWidth,
            sheetHeight: exportHeight,
        };
        
        onExport(selectedFormatId, settings);
        if (onClose) onClose();
    };

    const handleDropdownChange = (newId: string) => {
        setSelectedFormatId(newId);
    };

    const isSpriteSheetSelected = selectedFormatId === SPRITE_SHEET_ID;

    // Calculate individual frame size for non-sprite-sheet displays
    const frameWidth = isSpriteSheetSelected && autoCols > 0 
        ? Math.round(Number(exportWidth) / autoCols) 
        : Math.round(Number(exportWidth) / Math.max(1, autoCols));
    const frameHeight = isSpriteSheetSelected && autoRows > 0 
        ? Math.round(Number(exportHeight) / autoRows) 
        : Math.round(Number(exportHeight) / Math.max(1, autoRows));

    return (
        <MenuOpen
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            color='white'
            minWidth={null}
            placement='bottom'
            onMenuLeave={onMenuLeave}
        >
            <MenuContainer>
                <MenuTitle>Export Animation</MenuTitle>
                <ExportOptionsContainer>
                    <Item>
                        {/* <Dropdown
                            options={exportOptions}
                            value={selectedFormatId}
                            onChange={handleDropdownChange}
                            colorMode='light'
                            minWidth='fit-content'
                            width='100%'
                            maxWidth='100%'
                        /> */}
                        <SelectButtonGroup
                            options={exportOptions}
                            value={selectedFormatId}
                            onChange={handleDropdownChange}
                            colorMode="light"
                            width="100%"
                            orientation="vertical"
                        />
                    </Item>
                    
                    {isSpriteSheetSelected ? (
                        <>
                            <GridSelect 
                                rows={10}   
                                cols={10}   
                                onSelect={handleGridSelect}
                                colorMode='light'
                                initialSelection={selectedGrid}
                                actualCount={totalFrames}
                                autoFit={true}
                                disabled={true}
                            />

                            <Item>
                                <LabelRow
                                    label='Size'
                                    units={exportWidth + ' x ' + exportHeight + 'px'}
                                    size='mini'
                                />
                            </Item>
                        </>
                    ) : (
                        <Item>
                            <LabelRow
                                label='Size'
                                units={frameWidth + ' x ' + frameHeight + 'px'}
                                size='mini'
                            />
                        </Item>
                    )}
                </ExportOptionsContainer>
                <DownloadButtonContainer>
                    <Button 
                        size='micro'
                        color='black'
                        text='Download'
                        onClick={handleDownload}
                        fullWidth={true}
                        disabled={isExporting}
                    />
                </DownloadButtonContainer>
            </MenuContainer>
        </MenuOpen>
    );
};

export default ExportMenu;